import { useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ETICHETE_FAZA } from '@b4l/shared'
import { TON } from '../../ui/TonuriB4L'
import { perioada } from './comun'
import { TON_FAZA, type ActivitateCuFaza } from './activitate'

const ZI = 86_400_000
const SAPTAMANI = 12

function luni(d: Date): Date {
  const z = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  z.setDate(z.getDate() - ((z.getDay() + 6) % 7))
  return z
}

/**
 * Unde se termina bara. Fara data de final, o activitate inceputa (in desfasurare) tine pe termen
 * nedefinit — bara merge pana la marginea calendarului; una neinceputa ocupa ziua de start.
 */
function sfarsitul(a: ActivitateCuFaza): number {
  if (a.dataFinal) return new Date(a.dataFinal).getTime() + ZI
  if (a.faza === 'desfasurare') return 8.64e15 // cea mai mare data valida in JS
  return new Date(a.dataStart).getTime() + ZI
}

/**
 * Activitatile pe o axa de timp, cate 12 saptamani (Madalin, 15.10.2026: „geniala ideea sa avem
 * si calendar"). Se vede ce se suprapune si ce urmeaza; bara are culoarea fazei, clic pe ea
 * deschide rezumatul.
 */
export function CalendarActivitati({
  activitati,
  onAlege,
}: {
  activitati: ActivitateCuFaza[]
  onAlege: (id: number) => void
}) {
  const azi = new Date()
  // Fereastra porneste cu doua saptamani in urma: ce tocmai s-a terminat inca e de inchis.
  const [inceput, setInceput] = useState(() => new Date(luni(azi).getTime() - 14 * ZI))
  const sfarsit = useMemo(() => new Date(inceput.getTime() + SAPTAMANI * 7 * ZI), [inceput])
  const total = SAPTAMANI * 7

  const randuri = useMemo(
    () =>
      activitati
        .filter((a) => {
          const s = new Date(a.dataStart).getTime()
          const f = sfarsitul(a)
          return s < sfarsit.getTime() && f > inceput.getTime()
        })
        .sort((a, b) => a.dataStart.localeCompare(b.dataStart)),
    [activitati, inceput, sfarsit],
  )

  const pozitie = (d: Date) =>
    Math.max(0, Math.min(total, (d.getTime() - inceput.getTime()) / ZI)) / total
  const saptamani = Array.from(
    { length: SAPTAMANI },
    (_, i) => new Date(inceput.getTime() + i * 7 * ZI),
  )
  const lunaAni = (d: Date) => d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })
  const muta = (s: number) => setInceput(new Date(inceput.getTime() + s * 7 * ZI))
  const pozAzi = pozitie(azi)

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <span className="text-mic text-b4l-text-muted">
          {lunaAni(inceput)} – {lunaAni(new Date(sfarsit.getTime() - ZI))} · {randuri.length}{' '}
          {randuri.length === 1 ? 'activitate' : 'activități'}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => muta(-4)}
            aria-label="Patru săptămâni înapoi"
            className="rounded-md p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => setInceput(new Date(luni(azi).getTime() - 14 * ZI))}
            className="rounded-md px-2 py-1 text-mic text-b4l-text-muted hover:bg-b4l-bg"
          >
            Azi
          </button>
          <button
            type="button"
            onClick={() => muta(4)}
            aria-label="Patru săptămâni înainte"
            className="rounded-md p-1.5 text-b4l-text-muted hover:bg-b4l-bg"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="min-w-[820px]">
          <div className="grid grid-cols-[220px_1fr] text-[11px] text-b4l-text-dim">
            <span className="px-4 py-1.5">Activitatea</span>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${SAPTAMANI}, 1fr)` }}>
              {saptamani.map((s) => {
                const acum = azi >= s && azi.getTime() < s.getTime() + 7 * ZI
                return (
                  <span
                    key={s.getTime()}
                    className={`border-l border-b4l-border px-1.5 py-1.5 ${acum ? 'font-semibold text-b4l-blue-dark' : ''}`}
                  >
                    {lunaAni(s)}
                    {acum && ' · azi'}
                  </span>
                )
              })}
            </div>
          </div>
          {randuri.length === 0 && (
            <p className="px-4 py-6 text-center text-mic text-b4l-text-muted">
              Nicio activitate în perioada asta.
            </p>
          )}
          {randuri.map((a) => {
            const st = pozitie(new Date(a.dataStart))
            const fi = pozitie(new Date(sfarsitul(a)))
            // ~11 px pe zi la latimea minima: sub 12 zile, perioada nu incape in bara.
            const lata = (fi - st) * total >= 12
            return (
              <div key={a.id} className="grid grid-cols-[220px_1fr] border-t border-b4l-border">
                <button
                  type="button"
                  onClick={() => onAlege(a.id)}
                  className="truncate px-4 py-2 text-left text-mic hover:underline"
                  title={a.titlu}
                >
                  {a.titlu}
                </button>
                <div className="relative overflow-hidden">
                  <div
                    className="absolute inset-0 grid"
                    style={{ gridTemplateColumns: `repeat(${SAPTAMANI}, 1fr)` }}
                    aria-hidden
                  >
                    {saptamani.map((s) => (
                      <i key={s.getTime()} className="border-l border-b4l-border" />
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => onAlege(a.id)}
                    title={`${a.titlu} · ${perioada(a)} · ${ETICHETE_FAZA[a.faza]}`}
                    aria-label={`${a.titlu}, ${perioada(a)}, ${ETICHETE_FAZA[a.faza]}`}
                    className={`absolute inset-y-1.5 flex items-center overflow-hidden whitespace-nowrap rounded-md px-1.5 text-[11px] font-semibold ${TON[TON_FAZA[a.faza]].pastila}`}
                    style={{ left: `${st * 100}%`, width: `max(${(fi - st) * 100}%, 10px)` }}
                  >
                    {lata && perioada(a)}
                  </button>
                  {/*
                    O bara scurta (cateva zile) nu-si incape data: textul sta langa ea, nu taiat
                    inauntru.
                  */}
                  {!lata && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 flex items-center whitespace-nowrap pl-1.5 text-[11px] text-b4l-text-muted"
                      style={{ left: `calc(max(${fi * 100}%, ${st * 100}% + 10px))` }}
                    >
                      {perioada(a)}
                    </span>
                  )}
                  {pozAzi > 0 && pozAzi < 1 && (
                    <span
                      aria-hidden
                      className="absolute inset-y-0 w-0.5 bg-b4l-blue/60"
                      style={{ left: `${pozAzi * 100}%` }}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
      <p className="px-4 pb-3 text-[11.5px] text-b4l-text-dim">
        Culoarea barei arată faza. Clic pe bară sau pe nume pentru rezumat.
      </p>
    </div>
  )
}
